import { forwardRef } from 'react';
import { DenominationTable } from '@/components/store/DenominationCounter';
import { formatDate, formatCurrency } from '@/utils/dateFormat';

/**
 * Printable Cash Register Closing Report
 * Clear breakdown: Opening + Cash Sales - Return Refunds - Exchange Refunds - Expenses = Expected Cash
 */
const CashRegisterReport = forwardRef(({ register, storeName, storeCode }, ref) => {
  if (!register) return null;

  // Cash flow values
  const openingBalance = register.opening_balance || 0;
  const cashSales = register.total_cash_sales || register.current_cash_sales || 0;
  
  // Other payment modes (for reference only - not affecting cash)
  const cardSales = register.total_card_sales || 0;
  const upiSales = register.total_upi_sales || 0;
  const otherSales = register.total_other_sales || 0;
  const totalAllSales = cashSales + cardSales + upiSales + otherSales;
  
  // Refunds - Calculate display values correctly
  // Note: total_cash_returns includes ALL cash refunds (returns + exchanges)
  // total_exchange_refunds tracks exchange refunds specifically
  // So: Return Refunds = total_cash_returns - total_exchange_refunds
  const totalCashReturns = register.total_cash_returns || 0;
  const trackedExchangeRefunds = register.total_exchange_refunds || 0;
  
  // Return refunds = total cash out minus exchange refunds
  const returnRefunds = Math.max(0, totalCashReturns - trackedExchangeRefunds);
  const exchangeRefunds = trackedExchangeRefunds;
  
  // For display we show both separately, but total_cash_returns is used for cash calculation
  const totalRefundsForDisplay = returnRefunds + exchangeRefunds;
  
  // Expenses
  const totalCashExpenses = register.total_cash_expenses || 0;
  
  // Cash calculations
  // Note: Use totalCashReturns for calculation (includes all refunds), not display values
  const cashIn = openingBalance + cashSales;
  const cashOut = totalCashReturns + totalCashExpenses;
  const expectedCash = cashIn - cashOut;
  const actualCash = register.actual_closing_balance || 0;
  const variance = register.cash_variance || (actualCash - expectedCash);
  
  const isManagerClose = register.is_manager_close || register.closed_by !== register.opened_by;

  return (
    <div ref={ref} className="bg-white p-8 max-w-[800px] mx-auto print:p-4 print:max-w-none cash-register-print-report">
      {/* Print-only styles: force every caption/number to pure black so the
          Cash Register Closing Report is legible on monochrome printers.
          Backgrounds are kept via print-color-adjust: exact so section
          highlighting still prints, but all text becomes bold-black. */}
      <style>{`
        @media print {
          .cash-register-print-report,
          .cash-register-print-report * {
            color: #000 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .cash-register-print-report .text-xs,
          .cash-register-print-report .text-sm {
            font-weight: 500 !important;
          }
          .cash-register-print-report h1,
          .cash-register-print-report h2,
          .cash-register-print-report h3,
          .cash-register-print-report h4,
          .cash-register-print-report .font-medium,
          .cash-register-print-report .font-semibold,
          .cash-register-print-report .font-bold {
            font-weight: 700 !important;
          }
          .cash-register-print-report .border-gray-200,
          .cash-register-print-report .border-gray-300,
          .cash-register-print-report .border-gray-400 {
            border-color: #000 !important;
          }
        }
      `}</style>
      {/* Header */}
      <div className="text-center border-b-2 border-gray-800 pb-4 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{storeName || 'SY-Store'}</h1>
        <p className="text-sm text-gray-600">{storeCode}</p>
        <h2 className="text-xl font-semibold mt-3 text-gray-800">CASH REGISTER CLOSING REPORT</h2>
        <p className="text-sm text-gray-500 mt-1">Terminal: {register.counter_id}</p>
      </div>

      {/* Session Info */}
      <div className="grid grid-cols-2 gap-6 mb-6 bg-gray-50 p-4 rounded-lg">
        <div className="space-y-2">
          <div className="flex justify-between border-b border-gray-200 pb-1">
            <span className="text-gray-600">Terminal ID:</span>
            <span className="font-medium">{register.counter_id}</span>
          </div>
          <div className="flex justify-between border-b border-gray-200 pb-1">
            <span className="text-gray-600">Shift:</span>
            <span className="font-medium">{register.shift_id}</span>
          </div>
          <div className="flex justify-between border-b border-gray-200 pb-1">
            <span className="text-gray-600">Opened By:</span>
            <span className="font-medium">{register.opened_by_name}</span>
          </div>
          <div className="flex justify-between border-b border-gray-200 pb-1">
            <span className="text-gray-600">Opening Time:</span>
            <span className="font-medium">{formatDate(register.opening_time)}</span>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex justify-between border-b border-gray-200 pb-1">
            <span className="text-gray-600">Register Date:</span>
            <span className="font-medium">{register.register_date}</span>
          </div>
          <div className="flex justify-between border-b border-gray-200 pb-1">
            <span className="text-gray-600">Closed By:</span>
            <span className={`font-medium ${isManagerClose ? 'text-amber-600' : ''}`}>
              {register.closed_by_name || '-'}
              {isManagerClose && ' (Manager)'}
            </span>
          </div>
          <div className="flex justify-between border-b border-gray-200 pb-1">
            <span className="text-gray-600">Closing Time:</span>
            <span className="font-medium">{register.closing_time ? formatDate(register.closing_time) : '-'}</span>
          </div>
          <div className="flex justify-between border-b border-gray-200 pb-1">
            <span className="text-gray-600">Report Generated:</span>
            <span className="font-medium">{formatDate(new Date())}</span>
          </div>
        </div>
      </div>

      {/* CASH FLOW STATEMENT - Main Section */}
      <div className="mb-6">
        <h3 className="text-lg font-bold text-gray-800 border-b-2 border-blue-500 pb-2 mb-4 bg-blue-50 px-3 py-2 rounded-t">
          CASH FLOW STATEMENT
        </h3>
        
        {/* Cash In */}
        <div className="bg-green-50 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-green-800 mb-3 border-b border-green-200 pb-2">CASH IN (+)</h4>
          <div className="space-y-2">
            <div className="flex justify-between py-1">
              <span className="text-gray-700">Opening Balance</span>
              <span className="font-medium">{formatCurrency(openingBalance)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-gray-700">Cash Sales <span className="text-xs text-gray-500">(net of change returned{(register.cash_change_given || 0) > 0 ? ` ${formatCurrency(register.cash_change_given)}` : ''})</span></span>
              <span className="font-medium text-green-600" data-testid="report-cash-sales">+ {formatCurrency(cashSales)}</span>
            </div>
            <div className="flex justify-between py-2 bg-green-100 px-2 rounded font-bold">
              <span className="text-green-800">Total Cash In</span>
              <span className="text-green-700">{formatCurrency(cashIn)}</span>
            </div>
          </div>
        </div>

        {/* Cash Out */}
        <div className="bg-red-50 rounded-lg p-4 mb-4">
          <h4 className="font-semibold text-red-800 mb-3 border-b border-red-200 pb-2">CASH OUT (-)</h4>
          <div className="space-y-2">
            <div className="flex justify-between py-1">
              <span className="text-gray-700">Return Refunds (Cash)</span>
              <span className="font-medium text-orange-600">- {formatCurrency(returnRefunds)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-gray-700">Exchange Refunds (Cash)</span>
              <span className="font-medium text-purple-600">- {formatCurrency(exchangeRefunds)}</span>
            </div>
            <div className="flex justify-between py-1">
              <span className="text-gray-700">Cash Expenses</span>
              <span className="font-medium text-red-600">- {formatCurrency(totalCashExpenses)}</span>
            </div>
            <div className="flex justify-between py-2 bg-red-100 px-2 rounded font-bold">
              <span className="text-red-800">Total Cash Out</span>
              <span className="text-red-700">{formatCurrency(cashOut)}</span>
            </div>
          </div>
        </div>

        {/* Expected vs Actual */}
        <div className="bg-blue-50 rounded-lg p-4">
          <div className="space-y-3">
            <div className="flex justify-between py-2 bg-blue-100 px-3 rounded">
              <span className="font-bold text-blue-800">EXPECTED CASH IN DRAWER</span>
              <span className="font-bold text-blue-700 text-lg">{formatCurrency(expectedCash)}</span>
            </div>
            <div className="flex justify-between py-2 px-3 border-b border-blue-200">
              <span className="text-gray-700">Actual Cash Counted (Entered)</span>
              <span className="font-medium text-lg">{formatCurrency(actualCash)}</span>
            </div>
            <div className={`flex justify-between py-3 px-3 rounded-lg ${
              variance === 0 ? 'bg-green-100' : variance > 0 ? 'bg-blue-100' : 'bg-red-100'
            }`}>
              <span className="font-bold text-gray-800">CASH VARIANCE</span>
              <span className={`font-bold text-xl ${
                variance === 0 ? 'text-green-600' : variance > 0 ? 'text-blue-600' : 'text-red-600'
              }`}>
                {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
                {variance === 0 && ' ✓ Balanced'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Sales Summary (Reference Only) */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold text-gray-800 border-b-2 border-gray-300 pb-2 mb-4">
          SALES SUMMARY (All Payment Modes)
        </h3>
        <div className="bg-gray-50 rounded-lg p-4">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-300">
                <th className="text-left py-2 text-gray-600">Payment Method</th>
                <th className="text-right py-2 text-gray-600">Amount (₹)</th>
                <th className="text-right py-2 text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-gray-200">
                <td className="py-2 font-medium">Cash Sales</td>
                <td className="text-right py-2 font-medium">{formatCurrency(cashSales)}</td>
                <td className="text-right py-2 text-green-600 text-sm">In Register</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-2">Card Sales</td>
                <td className="text-right py-2">{formatCurrency(cardSales)}</td>
                <td className="text-right py-2 text-blue-600 text-sm">Bank Settlement</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-2">UPI Sales</td>
                <td className="text-right py-2">{formatCurrency(upiSales)}</td>
                <td className="text-right py-2 text-purple-600 text-sm">Bank Settlement</td>
              </tr>
              <tr className="border-b border-gray-200">
                <td className="py-2">Other Methods</td>
                <td className="text-right py-2">{formatCurrency(otherSales)}</td>
                <td className="text-right py-2 text-gray-500 text-sm">-</td>
              </tr>
              <tr className="bg-gray-100 font-bold">
                <td className="py-3">TOTAL SALES</td>
                <td className="text-right py-3 text-lg">{formatCurrency(totalAllSales)}</td>
                <td className="text-right py-3"></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="mb-6 grid grid-cols-5 gap-3">
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
          <p className="text-xs text-green-600 mb-1">Cash Sales</p>
          <p className="text-lg font-bold text-green-700">{formatCurrency(cashSales)}</p>
        </div>
        <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
          <p className="text-xs text-orange-600 mb-1">Return Refunds</p>
          <p className="text-lg font-bold text-orange-700">{formatCurrency(returnRefunds)}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 text-center">
          <p className="text-xs text-purple-600 mb-1">Exchange Refunds</p>
          <p className="text-lg font-bold text-purple-700">{formatCurrency(exchangeRefunds)}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-center">
          <p className="text-xs text-red-600 mb-1">Cash Expenses</p>
          <p className="text-lg font-bold text-red-700">{formatCurrency(totalCashExpenses)}</p>
        </div>
        <div className={`border rounded-lg p-3 text-center ${
          variance === 0 ? 'bg-emerald-50 border-emerald-200' : 
          variance > 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'
        }`}>
          <p className={`text-xs mb-1 ${
            variance === 0 ? 'text-emerald-600' : variance > 0 ? 'text-blue-600' : 'text-red-600'
          }`}>Variance</p>
          <p className={`text-lg font-bold ${
            variance === 0 ? 'text-emerald-700' : variance > 0 ? 'text-blue-700' : 'text-red-700'
          }`}>
            {variance >= 0 ? '+' : ''}{formatCurrency(variance)}
          </p>
        </div>
      </div>

      {/* Cash denominations counted at close */}
      {register.denominations && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 border-b-2 border-gray-300 pb-2 mb-4">
            CASH DENOMINATION COUNT
          </h3>
          <DenominationTable denominations={register.denominations} />
        </div>
      )}

      {/* Abnormal Activity Flags */}
      {register.abnormal_reasons && register.abnormal_reasons.length > 0 && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-lg p-4">
          <h3 className="text-lg font-semibold text-amber-800 mb-2">⚠️ FLAGGED ISSUES</h3>
          <ul className="list-disc list-inside space-y-1">
            {register.abnormal_reasons.map((reason, idx) => (
              <li key={idx} className="text-amber-700">{reason}</li>
            ))}
          </ul>
        </div>
      )}

      {/* Notes */}
      {(register.opening_notes || register.closing_notes) && (
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-800 border-b-2 border-gray-300 pb-2 mb-4">
            NOTES
          </h3>
          <div className="space-y-3">
            {register.opening_notes && (
              <div className="bg-gray-50 p-3 rounded">
                <span className="font-medium text-gray-700">Opening Notes: </span>
                <span className="text-gray-600">{register.opening_notes}</span>
              </div>
            )}
            {register.closing_notes && (
              <div className="bg-gray-50 p-3 rounded">
                <span className="font-medium text-gray-700">Closing Notes: </span>
                <span className="text-gray-600">{register.closing_notes}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Signature Lines */}
      <div className="mt-12 pt-6 border-t-2 border-gray-300">
        <h3 className="text-lg font-semibold text-gray-800 mb-6">SIGNATURES</h3>
        <div className="grid grid-cols-2 gap-12">
          <div>
            <div className="border-b-2 border-gray-400 mb-2 h-16"></div>
            <p className="text-center font-medium text-gray-700">Cashier Signature</p>
            <p className="text-center text-sm text-gray-500">{register.opened_by_name}</p>
          </div>
          <div>
            <div className="border-b-2 border-gray-400 mb-2 h-16"></div>
            <p className="text-center font-medium text-gray-700">Manager/Supervisor</p>
            <p className="text-center text-sm text-gray-500">
              {isManagerClose ? register.closed_by_name : '________________'}
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className="mt-8 pt-4 border-t border-gray-200 text-center text-xs text-gray-400">
        <p>This is a computer-generated report | Terminal: {register.counter_id} | Generated: {formatDate(new Date())}</p>
        <p className="mt-1">SY-Store POS System</p>
      </div>
    </div>
  );
});

CashRegisterReport.displayName = 'CashRegisterReport';

export default CashRegisterReport;
